
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { useUser } from '@/context/UserContext';
import { calculateStreakBonus } from '@/utils/gamification';
import CheckInForm from '@/components/CheckInForm';
import analytics from '@/utils/analytics';
import errorMonitoring from '@/utils/errorMonitoring';
import { supabase } from "@/integrations/supabase/client";
import { Lesson } from '@/data/lessons';

interface LessonCheckInHandlerProps {
  currentLesson: Lesson;
  dayNumber: number;
  isDayCompleted: boolean;
  earnedPoints: number;
  setEarnedPoints: React.Dispatch<React.SetStateAction<number>>;
  setCompletedDay: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

const LessonCheckInHandler: React.FC<LessonCheckInHandlerProps> = ({ 
  currentLesson, 
  dayNumber, 
  isDayCompleted, 
  earnedPoints, 
  setEarnedPoints, 
  setCompletedDay, 
  setError 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { userProgress, completeDay, user } = useUser();

  const handleCheckInSubmit = async (answer: string) => {
    if (isDayCompleted) {
      toast.info("You've already completed this day!");
      return;
    }

    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      // Track check-in submission
      analytics.trackLessonEngagement(dayNumber, 'check_in_submit', { answer_length: answer.length });
      
      // Calculate points: base points from lesson + streak bonus
      const basePoints = currentLesson.points || 10; // Default to 10 if not specified
      const streakBonus = calculateStreakBonus(userProgress.streak);
      const totalPoints = basePoints + streakBonus + earnedPoints; // Add any bonus points from program check-in
      
      // Save selected symptoms from DailyContent to symptom_logs if the user has selected any
      if (user?.id && window.selectedSymptoms && window.selectedSymptoms.length > 0) {
        await supabase.from('symptom_logs').insert({
          user_id: user.id,
          symptoms: window.selectedSymptoms.filter(s => s !== 'Other'),
          custom_symptoms: window.selectedSymptoms.filter(s => !['Fatigue', 'Headache', 'Bloating', 'Cramps', 
                               'Mood Swings', 'Insomnia', 'Low Energy', 'Cravings'].includes(s)),
          logged_at: new Date().toISOString(),
          notes: `Recorded during Day ${dayNumber}`
        });

        // Clear the global variable after saving
        window.selectedSymptoms = [];
      }
      
      // Update user progress
      completeDay(dayNumber, totalPoints);
      
      // Show success feedback
      setCompletedDay(true);
      setEarnedPoints(totalPoints);
      
      // Show different toast messages based on achievements
      if (userProgress.streak === 6) { // Will be 7 after the update
        toast.success(`Bronze Medal Earned! 7-day streak! +${totalPoints} points`);
        analytics.trackUserAction('achievement_earned', { type: 'bronze_medal', streak: 7 });
      } else if (userProgress.streak === 13) { // Will be 14 after the update
        toast.success(`Silver Medal Earned! 14-day streak! +${totalPoints} points`);
        analytics.trackUserAction('achievement_earned', { type: 'silver_medal', streak: 14 });
      } else if (userProgress.streak === 29) { // Will be 30 after the update
        toast.success(`Gold Medal Earned! You completed the entire program! +${totalPoints} points`);
        analytics.trackUserAction('achievement_earned', { type: 'gold_medal', streak: 30 });
      } else {
        toast.success(`Day ${dayNumber} completed! +${totalPoints} points`);
      }
      
      // If there was a streak bonus, show that too
      if (streakBonus > 0) {
        toast.success(`Streak bonus: +${streakBonus} points!`);
      }
      
      // Move to the next day after submission with a delay
      setTimeout(() => {
        if (dayNumber < 30) {
          navigate(`/lesson/${dayNumber + 1}`);
        } else {
          navigate('/dashboard');
        }
      }, 3000);
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error submitting check-in');
      console.error("Error submitting check-in:", err);
      setError("Failed to submit check-in. Please try again.");
      toast.error("Failed to submit check-in. Please try again.");
      errorMonitoring.reportError(err);
      analytics.trackUserAction('error', { action: 'check_in_submit', message: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isDayCompleted) {
    return null;
  }

  return (
    <CheckInForm 
      question={currentLesson.checkInQuestion} 
      onSubmit={handleCheckInSubmit}
      lessonDay={dayNumber}
    />
  );
};

export default LessonCheckInHandler;
